import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Image,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { FaReact, FaCloud, FaNodeJs } from 'react-icons/fa';
import { SiMongodb, SiClaude } from 'react-icons/si';

// Define keyframe animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: translateX(-50%) translateY(0); }
  50% { opacity: 0.5; transform: translateX(-50%) translateY(10px); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-5px); }
`;

const mouseHoverOnButton = keyframes`
  0% {
    top: 140%;
    left: 50%;
    transform: translate(-50%, 0);
  }
  40% {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  45% {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  50% {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  90% {
    top: 140%;
    left: 50%;
    transform: translate(-50%, 0);
  }
  100% {
    top: 140%;
    left: 50%;
    transform: translate(-50%, 0);
  }
`;

const buttonClickEffect = keyframes`
  0%, 44%, 51%, 100% {
    transform: scale(1);
  }
  45%, 50% {
    transform: scale(0.95);
  }
`;

const clickRipple = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
  }
  1% {
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(3);
    opacity: 0;
  }
`;

const Hero = () => {
  const [showIcons] = useState(true); // Always show icons
  const [showCodeLine] = useState(true); // Always show console.log

  // Handle profile image click
  const handleProfileClick = () => {
    console.log(
      '%c👋 Hello! Welcome to my portfolio!',
      'color: #5BC0EB; font-size: 20px; font-weight: bold;'
    );
    console.log(
      "%cLooking for a developer? Let's connect!",
      'color: #FFA500; font-size: 14px;'
    );
  };

  const techIcons = [
    { content: '</>', color: '#5BC0EB', fontSize: '2rem', fontWeight: 'bold' },
    { icon: FaCloud, color: '#FFA500' },
    { content: '{}', color: '#69c7bc', fontSize: '2.5rem', fontWeight: 'bold', fontFamily: 'monospace' },
    { icon: FaReact, color: '#61DAFB' },
    { icon: SiClaude, color: '#FF6B35' },
    { icon: FaNodeJs, color: '#339933' },
    { icon: SiMongodb, color: '#47A248' },
  ];

  return (
    <Box
      as="section"
      minH="100vh"
      bgGradient="linear(135deg, #0c1e3d 0%, #1a2f4d 50%, #2d4a6d 100%)"
      position="relative"
      overflow="hidden"
      display="flex"
      alignItems="center"
      pt="120px"
      pb="80px"
    >
      <Container maxW="1200px" h="100%" position="relative" zIndex={2}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          minH="calc(100vh - 100px)"
          flexDirection={{ base: 'column', md: 'row' }}
          gap={8}
        >
          {/* Left Side - Hero Text */}
          <VStack align={{ base: 'center', md: 'flex-start' }} spacing={6} flex={1} textAlign={{ base: 'center', md: 'left' }}>
            <Heading
              as="h1"
              fontSize={{ base: '4xl', md: '6xl' }}
              fontWeight="900"
              color="white"
              lineHeight="1.1"
            >
              Hello World,
              <br />
              I'm{' '}
              <Box
                as="span"
                color="white"
                position="relative"
                display="inline-block"
                zIndex={1}
                _before={{
                  content: '""',
                  position: 'absolute',
                  bottom: '5px',
                  left: '-5px',
                  right: '-5px',
                  height: '18px',
                  bg: '#69c7bc',
                  zIndex: -1,
                  transform: 'skewY(-1deg)',
                  borderRadius: '4px',
                }}
              >
                Ramil Sharapov
              </Box>
            </Heading>

            <Text
              fontSize={{ base: 'xl', md: '2rem' }}
              color="whiteAlpha.900"
              maxW="600px"
              fontWeight="400"
            >
              I Design and Develop Full-Stack Applications
            </Text>

            <Box position="relative" display="inline-block">
              <Button
                as="a"
                href="#contact"
                size="lg"
                bg="brand.teal"
                color="white"
                px={10}
                py={6}
                fontSize="lg"
                borderRadius="full"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: '0 10px 30px rgba(105, 199, 188, 0.4)',
                }}
                transition="all 0.3s"
                animation={`${buttonClickEffect} 6s ease-in-out infinite`}
              >
                Let's Connect
              </Button>

              {/* Animated Mouse Cursor hovering over button */}
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                zIndex={150}
                pointerEvents="none"
                animation={`${mouseHoverOnButton} 6s ease-in-out infinite`}
                display={{ base: 'none', md: 'block' }}
              >
                {/* Mouse Icon */}
                <Box
                  w="40px"
                  h="60px"
                  border="3px solid"
                  borderColor="#5BC0EB"
                  borderRadius="20px"
                  position="relative"
                  bg="white"
                  boxShadow="0 4px 15px rgba(91, 192, 235, 0.4)"
                >
                  {/* Mouse Scroll Wheel */}
                  <Box
                    w="6px"
                    h="10px"
                    bg="#5BC0EB"
                    borderRadius="full"
                    position="absolute"
                    top="10px"
                    left="50%"
                    transform="translateX(-50%)"
                    animation={`${pulse} 2s ease-in-out infinite`}
                  />
                </Box>

                {/* Click Ripple Effect - only shows when clicking */}
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  w="20px"
                  h="20px"
                  border="2px solid"
                  borderColor="#5BC0EB"
                  borderRadius="50%"
                  opacity={0}
                  sx={{
                    animation: `clickTiming 6s ease-in-out infinite`,
                    '@keyframes clickTiming': {
                      '0%, 44%': {
                        opacity: 0,
                        transform: 'translate(-50%, -50%) scale(0)'
                      },
                      '45%': {
                        opacity: 1,
                        transform: 'translate(-50%, -50%) scale(0)'
                      },
                      '48%': {
                        opacity: 0,
                        transform: 'translate(-50%, -50%) scale(3)'
                      },
                      '100%': {
                        opacity: 0,
                        transform: 'translate(-50%, -50%) scale(0)'
                      }
                    }
                  }}
                />
              </Box>
            </Box>
          </VStack>

          {/* Right Side - Profile Image with Icons */}
          <Box position="relative" flex={1} display="flex" justifyContent="center" alignItems="center">
            <VStack spacing={6} align="center">
              {/* Profile Image Wrapper */}
              <Box position="relative">
                <Image
                  src="/Ramil1988.png"
                  alt="Ramil Sharapov"
                  boxSize={{ base: '300px', md: '450px' }}
                  objectFit="contain"
                  cursor="pointer"
                  onClick={handleProfileClick}
                  _hover={{ transform: 'scale(1.05)' }}
                  transition="transform 0.3s ease"
                  animation={`${fadeIn} 1s ease-out`}
                  fallbackSrc="https://via.placeholder.com/450"
                />
              </Box>

              {/* Floating Tech Icons - Horizontal Stack Below Image */}
              {showIcons && (
                <HStack
                  spacing={4}
                  animation={`${fadeIn} 0.5s ease-out`}
                  display={{ base: 'none', md: 'flex' }}
                >
                  {techIcons.map((tech, index) => (
                    <Box
                      key={index}
                      fontSize={tech.fontSize || "2xl"}
                      color={tech.color}
                      fontWeight={tech.fontWeight}
                      fontFamily={tech.fontFamily}
                      bg="white"
                      w="80px"
                      h="80px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      borderRadius="20px"
                      boxShadow="0 10px 30px rgba(0, 0, 0, 0.15)"
                      animation={`${float} ${3 + index * 0.3}s ease-in-out infinite`}
                    >
                      {tech.content ? tech.content : <Box as={tech.icon} fontSize="32px" />}
                    </Box>
                  ))}
                </HStack>
              )}

              {/* VS Code Line - Below Icons */}
              {showCodeLine && (
                <Box
                  bg="#1e1e1e"
                  borderRadius="8px"
                  p={4}
                  minW={{ base: '90%', md: '500px' }}
                  boxShadow="0 10px 40px rgba(0, 0, 0, 0.3)"
                  animation={`${fadeIn} 0.5s ease-out`}
                  display={{ base: 'none', md: 'block' }}
                >
                  <HStack spacing={2} fontFamily="'Fira Code', monospace" fontSize="sm">
                    <Text color="#858585">1</Text>
                    <Text color="#4EC9B0">console</Text>
                    <Text color="#D4D4D4">.</Text>
                    <Text color="#DCDCAA">log</Text>
                    <Text color="#D4D4D4">(</Text>
                    <Text color="#CE9178">"I Design and Develop Full-Stack Applications"</Text>
                    <Text color="#D4D4D4">)</Text>
                    <Text color="#D4D4D4">;</Text>
                  </HStack>
                </Box>
              )}
            </VStack>
          </Box>
        </Box>

        {/* Scroll Indicator */}
        <Box
          position="absolute"
          bottom="40px"
          left="50%"
          transform="translateX(-50%)"
          animation={`${bounce} 2s infinite`}
          display={{ base: 'none', md: 'block' }}
        >
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 5V19M12 19L5 12M12 19L19 12"
              stroke="#69c7bc"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>
      </Container>

      {/* Wave Decoration */}
      <Box
        position="absolute"
        bottom={0}
        left={0}
        width="100%"
        overflow="hidden"
        lineHeight={0}
      >
        <svg
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          style={{ width: '100%', height: '100px' }}
        >
          <path
            d="M0,0 C300,100 900,100 1200,0 L1200,120 L0,120 Z"
            fill="#69c7bc"
          />
        </svg>
      </Box>
    </Box>
  );
};

export default Hero;
